import {
  campaignCutNotice,
  syncCampaignCut,
  syncQuotaNotice,
  withoutChannelNotices,
  type UserNotice,
} from './notices';

import type { AccountCapacity, QuotaChannel } from '@/features/marketing-shared';

const STORAGE_KEY = 'fixe.user-notices.v1';

type Listener = () => void;

let cache: UserNotice[] = [];
let loaded = false;
const listeners = new Set<Listener>();

function readStorage(): UserNotice[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as UserNotice[]) : [];
  } catch {
    return [];
  }
}

function writeStorage(next: UserNotice[]) {
  cache = next;
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }
  listeners.forEach((listener) => listener());
}

function ensureLoaded() {
  if (loaded) return;
  loaded = true;
  cache = readStorage();
}

export function subscribeNotices(listener: Listener): () => void {
  ensureLoaded();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getNotices(): UserNotice[] {
  ensureLoaded();
  return cache;
}

export function unreadNoticeCount(): number {
  return getNotices().filter((item) => !item.read).length;
}

function commit(result: { next: UserNotice[]; created: UserNotice[] }): UserNotice[] {
  writeStorage(result.next);
  return result.created;
}

export function syncChannelQuotaNotice(
  passbookId: string,
  channel: QuotaChannel,
  quota: AccountCapacity,
): UserNotice[] {
  return commit(syncQuotaNotice(getNotices(), passbookId, channel, quota));
}

/** Canal desligado no passbook: some com os avisos já gravados neste navegador. */
export function clearChannelNotices(passbookId: string, channel: QuotaChannel) {
  const current = getNotices();
  const next = withoutChannelNotices(current, passbookId, channel);
  if (next !== current) writeStorage(next);
}

export function recordCampaignCut(input: {
  passbookId: string;
  channel: QuotaChannel;
  campaignName: string;
  sendPeople: number;
  leftOut: number;
}): UserNotice[] {
  return commit(syncCampaignCut(getNotices(), campaignCutNotice(input)));
}

export function markNoticeRead(id: string) {
  writeStorage(getNotices().map((item) => (item.id === id ? { ...item, read: true } : item)));
}

export function markAllNoticesRead() {
  writeStorage(getNotices().map((item) => ({ ...item, read: true })));
}
